import React, { useState, useEffect } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { CalculatorEngine } from '../utils/calculator';

const MainDisplay: React.FC = () => {
  const { state, dispatch } = useApp();
  const [calculator] = useState(() => new CalculatorEngine());
  const [showPreview, setShowPreview] = useState(false);

  // Update calculator variables when they change
  useEffect(() => {
    calculator.setVariables(state.variables);
  }, [state.variables, calculator]);

  // Real-time preview calculation
  useEffect(() => {
    if (state.calculator.currentExpression && !state.calculator.isError) {
      const { result, isError } = calculator.evaluate(state.calculator.currentExpression);
      if (!isError && result !== state.calculator.currentResult) {
        setShowPreview(true);
        const timer = setTimeout(() => setShowPreview(false), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [state.calculator.currentExpression, state.calculator.currentResult, state.calculator.isError, calculator]);

  const handleExpressionChange = (expression: string) => {
    dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: expression });
    
    // Clear previous error
    if (state.calculator.isError) {
      dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
    }
  };

  const calculate = async () => {
    if (!state.calculator.currentExpression.trim()) return;

    const { result, isError, errorMessage } = calculator.evaluate(state.calculator.currentExpression);
    
    if (isError) {
      dispatch({ type: 'SET_ERROR', payload: { isError: true, message: errorMessage } });
    } else {
      dispatch({ type: 'SET_CURRENT_RESULT', payload: result });
      // Keep the result in the expression so user can continue calculating
      dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: result });
      
      // Save to Firestore if in a session
      if (state.currentSession && state.currentUser.id) {
        try {
          const calculation = {
            id: Date.now().toString(),
            expression: state.calculator.currentExpression,
            result,
            timestamp: new Date(),
            userId: state.currentUser.id,
            userName: state.currentUser.name,
            userColor: state.currentUser.color,
            isApproved: false,
            approvedBy: [],
            mode: state.calculator.mode,
          };
          
          const { calculatorService } = await import('../services/calculatorService');
          await calculatorService.addCalculation(state.currentSession.id, calculation);
          // The Firebase listener will handle updating the local state
        } catch (error) {
          console.error('Failed to save calculation to Firestore:', error);
          // If Firestore fails, add to local state as fallback
          const calculation = {
            id: Date.now().toString(),
            expression: state.calculator.currentExpression,
            result,
            timestamp: new Date(),
            userId: state.currentUser.id,
            userName: state.currentUser.name,
            userColor: state.currentUser.color,
            isApproved: false,
            approvedBy: [],
            mode: state.calculator.mode,
          };
          dispatch({ type: 'ADD_CALCULATION', payload: calculation });
        }
      } else {
        // If not in a session, add to local state only
        const calculation = {
          id: Date.now().toString(),
          expression: state.calculator.currentExpression,
          result,
          timestamp: new Date(),
          userId: state.currentUser.id,
          userName: state.currentUser.name,
          userColor: state.currentUser.color,
          isApproved: false,
          approvedBy: [],
          mode: state.calculator.mode,
        };
        dispatch({ type: 'ADD_CALCULATION', payload: calculation });
      }
    }
  };

  const clear = () => {
    dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: '' });
    dispatch({ type: 'SET_CURRENT_RESULT', payload: '' });
    dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
  };

  const toggleLock = () => {
    if (state.calculator.isLocked && state.calculator.lockedBy === state.currentUser.id) {
      dispatch({ type: 'SET_LOCK', payload: { isLocked: false } });
    } else if (!state.calculator.isLocked) {
      dispatch({ type: 'SET_LOCK', payload: { isLocked: true, lockedBy: state.currentUser.id } });
    }
  };

  const canEdit = !state.calculator.isLocked || state.calculator.lockedBy === state.currentUser.id;

  return (
    <div className="bg-dark-800 rounded-xl p-1 sm:p-2 space-y-1 sm:space-y-2 shadow-lg border border-dark-700">
      {/* Mode Tabs */}
      <div className="flex space-x-0.5 sm:space-x-1">
        {(['basic', 'scientific', 'programmer'] as const).map((mode) => (
          <button
            key={mode}
            onClick={() => dispatch({ type: 'SET_MODE', payload: mode })}
            className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
              state.calculator.mode === mode
                ? 'bg-accent-600 text-white shadow-md'
                : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
            }`}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {/* Display Area */}
      <div className="bg-dark-900 rounded-xl p-1.5 sm:p-2 space-y-1.5 sm:space-y-2 border border-dark-600">
        {/* Expression Input */}
        <div className="relative">
          <input
            type="text"
            value={state.calculator.currentExpression}
            onChange={(e) => handleExpressionChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') calculate();
              if (e.key === 'Escape') clear();
            }}
            disabled={!canEdit}
            className={`w-full bg-transparent text-2xl font-mono text-right pr-4 py-3 border-b-2 transition-all duration-200 ${
              state.calculator.isError
                ? 'border-danger-500 text-danger-400'
                : 'border-dark-600 text-white focus:border-accent-500'
            } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
            placeholder="Enter expression..."
          />
          
          {/* Cursor indicators for multiplayer */}
          {state.isMultiplayerMode && (state.users || []).map((user) => (
            user.isTyping && (
              <div
                key={user.id}
                className="absolute top-0 w-0.5 h-10 animate-cursor-blink"
                style={{ 
                  backgroundColor: user.color,
                  left: `${Math.min(user.cursorPosition || 0, state.calculator.currentExpression.length) * 12}px`
                }}
              />
            )
          ))}
        </div>

        {/* Result Display */}
        <div className="text-right">
          {state.calculator.isError ? (
            <div className="text-danger-400 text-lg font-mono">
              Error: {state.calculator.errorMessage}
            </div>
          ) : (
            <div className="text-success-400 text-4xl font-mono font-bold">
              {state.calculator.currentResult || '0'}
            </div>
          )}
          
          {/* Preview */}
          {showPreview && state.calculator.currentExpression && !state.calculator.isError && (
            <div className="text-dark-400 text-sm animate-typing">
              Preview: {calculator.evaluate(state.calculator.currentExpression).result}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-0.5 sm:space-x-1">
          <button
            onClick={calculate}
            disabled={!canEdit || !state.calculator.currentExpression.trim()}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-md text-xs sm:text-sm"
          >
            Calculate
          </button>
          
          <button
            onClick={clear}
            disabled={!canEdit}
            className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-dark-600 text-white rounded-lg hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-xs sm:text-sm"
          >
            Clear
          </button>
        </div>

        {/* Lock/Unlock Button */}
        {state.isMultiplayerMode && (
          <button
            onClick={toggleLock}
            className={`flex items-center space-x-1 px-1 py-1 rounded-lg transition-all duration-200 ${
              state.calculator.isLocked
                ? state.calculator.lockedBy === state.currentUser.id
                  ? 'bg-warning-600 text-white shadow-md'
                  : 'bg-danger-600 text-white'
                : 'bg-dark-600 text-dark-300 hover:bg-dark-500'
            }`}
          >
            {state.calculator.isLocked ? (
              <>
                <Lock className="h-3 w-3" />
                <span className="text-xs font-medium hidden sm:inline">
                  {state.calculator.lockedBy === state.currentUser.id ? 'Unlock' : 'Locked'}
                </span>
              </>
            ) : (
              <>
                <Unlock className="h-3 w-3" />
                <span className="text-xs font-medium hidden sm:inline">Lock</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default MainDisplay;
